import React, { useCallback, useEffect, useState } from 'react'
import { ArrowPathIcon, LinkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import toast from 'react-hot-toast'
import EvidenceLinkBrowser from '../../components/EvidenceLinkBrowser'
import { formatSydneyDateTime, formatSydneyTime } from '../../utils/sydneyDateTime'

import { evidenceItemTypeLabel } from '../../utils/evidenceBrowserLabels'

const fmt$ = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n) || 0)

const fmtTime = (v) => formatSydneyDateTime(v)

const typeLabel = evidenceItemTypeLabel

function candidateKey(c) {
  return String(c.id || `${c.type}|${c.sessionpk || ''}|${c.intervalstart || c.gaming_date || ''}`)
}

function CardedBadge({ candidate }) {
  if (candidate.type === 'slot_jackpot') {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
        EGM-day
      </span>
    )
  }
  if (candidate.carded === false || candidate.match_reasons?.includes('uncarded')) {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
        Uncarded
      </span>
    )
  }
  if (candidate.member_match === true) {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
        Carded · match
      </span>
    )
  }
  if (candidate.member_match === false) {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-50 text-orange-900 border border-orange-200">
        Carded · different member
      </span>
    )
  }
  return (
    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
      Carded · {candidate.MemberID || candidate._memberLabel || '—'}
    </span>
  )
}

/**
 * Payout modal — Transaction linking tab (suggest → select → Accept).
 * @see docs/Payout_Transaction_Linking.md
 */
export default function PayoutTransactionLinkingTab({
  payout,
  onLinkedChange,
}) {
  const payoutId = payout?.id
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [payload, setPayload] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [accepting, setAccepting] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [browseOpen, setBrowseOpen] = useState(false)

  const linked = Boolean(
    payout?.transactionLinked || payload?.payout?.transactionLinked
  )
  const acceptedSummary =
    payout?.linkedTransactionSummary || payload?.payout?.linkedTransactionSummary || null

  const payoutEgm = payout?.egmId || payout?.gameId || ''
  const missingEgm = !String(payoutEgm || '').trim()
  const memberHint = payout?.membershipNumber || ''
  const playerHint = payout?.playerId || payout?.memberId || ''

  const loadCandidates = useCallback(async () => {
    if (!payoutId) return
    setLoading(true)
    setError(null)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/monitoring/payouts/${encodeURIComponent(payoutId)}/link-candidates?windowMinutes=60`)
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || j.success === false) {
        throw new Error(j.error || `Failed to load candidates (${r.status})`)
      }
      setPayload(j)
      setSelected(new Set())
    } catch (e) {
      setError(e.message || 'Failed to load candidates')
      setPayload(null)
    } finally {
      setLoading(false)
    }
  }, [payoutId])

  useEffect(() => {
    loadCandidates()
  }, [loadCandidates])

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleAccept = async () => {
    const candidates = payload?.candidates || []
    const items = candidates.filter((c) => selected.has(candidateKey(c)))
    if (!items.length) {
      toast.error('Select at least one suggested transaction')
      return
    }
    setAccepting(true)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/monitoring/payouts/${encodeURIComponent(payoutId)}/link-transactions`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: items.map((it) => ({
              type: it.type,
              id: it.id,
              sessionpk: it.sessionpk || it.SessionPK,
              intervalstart: it.intervalstart || it.IntervalStart,
              trading_day: it.trading_day,
              source_id: it.source_id,
              source_table: it.source_table,
              EgmHouseNo: it.EgmHouseNo || it.EgmPK,
              MemberID: it.MemberID,
              CreditWin: it.display_amount ?? it.CreditWin,
              carded: it.carded,
              member_match: it.member_match,
              match_score: it.match_score,
              match_reasons: it.match_reasons,
            })),
          }),
        }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Accept failed')
      toast.success('Transaction link accepted')
      if (typeof onLinkedChange === 'function') onLinkedChange(j.data)
      setPayload((prev) =>
        prev
          ? {
              ...prev,
              payout: {
                ...(prev.payout || {}),
                transactionLinked: true,
                linkedTransactionSummary: j.data?.linkedTransactionSummary,
              },
            }
          : prev
      )
    } catch (e) {
      toast.error(e.message || 'Accept failed')
    } finally {
      setAccepting(false)
    }
  }

  const handleClear = async () => {
    if (!confirm('Clear the accepted transaction link for this payout?')) return
    setClearing(true)
    try {
      const r = await authFetch(
        getLocalApiUrl(`/api/monitoring/payouts/${encodeURIComponent(payoutId)}/link-transactions`),
        { method: 'DELETE' }
      )
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || 'Clear failed')
      toast.success('Transaction link cleared')
      if (typeof onLinkedChange === 'function') {
        onLinkedChange({
          transactionLinked: false,
          transactionLinkedAt: null,
          transactionLinkedBy: null,
          linkedTransactionSummary: null,
        })
      }
      setPayload((prev) =>
        prev
          ? {
              ...prev,
              payout: {
                ...(prev.payout || {}),
                transactionLinked: false,
                linkedTransactionSummary: null,
              },
            }
          : prev
      )
      setSelected(new Set())
    } catch (e) {
      toast.error(e.message || 'Clear failed')
    } finally {
      setClearing(false)
    }
  }

  const candidates = payload?.candidates || []
  const search = payload?.search

  return (
    <div className="space-y-4 min-h-[220px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <LinkIcon className="w-4 h-4 text-psg-teal" />
            Transaction linking
          </h4>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Find floor activity by EGM, time (about 1 hour before payout), and amount — including{' '}
            <strong>uncarded</strong> sits. Member id confirms carded matches; it is not the only search key.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
              linked
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : candidates.length
                  ? 'bg-sky-50 text-sky-800 border-sky-200'
                  : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}
          >
            {linked ? '✓ Linked (accepted)' : candidates.length ? `Suggested (${candidates.length})` : 'Not linked'}
          </span>
          <button
            type="button"
            onClick={loadCandidates}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {search && (
        <div className="text-[11px] text-gray-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex flex-wrap gap-x-4 gap-y-1">
          <span>
            EGM <span className="font-mono font-semibold">{search.egm || '—'}</span>
          </span>
          <span>Window {search.windowMinutes} min before payout</span>
          {payload?.jackpot_payout ? (
            <span className="text-amber-800 font-medium">Jackpot payout — slot day included</span>
          ) : null}
        </div>
      )}

      {!linked && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setBrowseOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border border-psg-teal text-psg-teal bg-white hover:bg-teal-50"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
            Lookup Combined / Sessions / Meter
          </button>
          <p className="text-[11px] text-gray-500 self-center">
            {missingEgm
              ? 'No EGM on this payout — browse by date (and optional member / EGM) then Accept.'
              : 'Widen the search or pick a sit when auto-suggest is incomplete.'}
          </p>
        </div>
      )}

      {linked && acceptedSummary && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
              Accepted link
            </div>
            <button
              type="button"
              onClick={handleClear}
              disabled={clearing}
              className="text-xs font-medium text-red-700 hover:underline disabled:opacity-50"
            >
              {clearing ? 'Clearing…' : 'Clear link'}
            </button>
          </div>
          <p className="text-[11px] text-emerald-900">
            Accepted by {acceptedSummary.accepted_by || payout?.transactionLinkedBy || '—'}
            {acceptedSummary.accepted_at
              ? ` · ${fmtTime(acceptedSummary.accepted_at)}`
              : payout?.transactionLinkedAt
                ? ` · ${fmtTime(payout.transactionLinkedAt)}`
                : ''}
          </p>
          <ul className="space-y-1">
            {(acceptedSummary.items || []).map((it, idx) => (
              <li key={idx} className="text-xs text-emerald-950 flex flex-wrap gap-2">
                <span className="font-medium">{typeLabel(it.type)}</span>
                <span className="font-mono text-[11px]">
                  {it.SessionPK || it.sessionpk || it.EgmHouseNo || it.EgmPK || it.id}
                </span>
                {it.StartTime || it.SnapshotTime || it.SnapShotTime ? (
                  <span className="text-emerald-800">
                    {fmtTime(it.StartTime || it.SnapshotTime || it.SnapShotTime)}
                  </span>
                ) : null}
                {it.CreditWin != null && <span>Win {fmt$(it.CreditWin)}</span>}
                {it.Turnover != null && it.type === 'session' && (
                  <span className="text-emerald-800">Turnover {fmt$(it.Turnover)}</span>
                )}
                {it.carded === false && <span className="text-slate-600">Uncarded</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-sm text-gray-500">
          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
          Looking up floor activity…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
          <button type="button" onClick={loadCandidates} className="block mt-2 text-psg-teal underline text-xs">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && payload?.message && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {payload.message}
        </p>
      )}

      {!loading && !error && (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
            Suggested matches
          </div>
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 px-3 py-8 text-center">
              {missingEgm
                ? 'No auto-suggestions without an EGM. Use Browse floor activity to link raw sessions, meter, or combined rows.'
                : 'No candidates yet. Use Browse floor activity (Sessions / Meter / Combined), or wait for end-of-day ingest.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {candidates.map((c) => {
                const key = candidateKey(c)
                const checked = selected.has(key)
                return (
                  <li key={key}>
                    <label className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        disabled={linked}
                        onChange={() => toggle(key)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900">{typeLabel(c.type)}</span>
                          <CardedBadge candidate={c} />
                          <span className="text-xs text-gray-500">score {c.match_score ?? '—'}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-mono">EGM {c.EgmHouseNo || c.EgmPK || '—'}</span>
                          <span>
                            {fmtTime(c.IntervalStart || c.SessionStart || c.timestamp || c.gaming_date)}
                            {(c.IntervalEnd || c.SessionEnd) &&
                              ` – ${formatSydneyTime(c.IntervalEnd || c.SessionEnd)}`}
                          </span>
                          <span className="font-semibold text-gray-800">
                            {fmt$(c.display_amount ?? c.CreditWin ?? c.jackpot_win_aud)}
                          </span>
                          {c.MemberID && c.carded !== false && (
                            <span>Member {c.MemberID}</span>
                          )}
                        </div>
                        {Array.isArray(c.match_reasons) && c.match_reasons.length > 0 && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            {c.match_reasons.join(' · ')}
                          </div>
                        )}
                      </div>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}

      {!linked && (
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={handleAccept}
            disabled={accepting || selected.size === 0}
            className="px-3 py-1.5 rounded-md bg-psg-teal text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {accepting ? 'Saving…' : `Accept selected (${selected.size})`}
          </button>
        </div>
      )}

      <EvidenceLinkBrowser
        display="modal"
        isOpen={browseOpen}
        onClose={() => setBrowseOpen(false)}
        context={{ type: 'payout', id: String(payoutId) }}
        title="Browse floor activity"
        defaultLane="sessions"
        lanes={['sessions', 'meter', 'combined', 'jackpots']}
        showCsvTab={false}
        seedMemberFilter={!missingEgm}
        defaultMemberNumber={memberHint}
        defaultPlayerId={playerHint}
        defaultMemberName={payout?.memberName || ''}
        defaultEgmHouseNo={payoutEgm}
        onLinked={(data) => {
          toast.success('Transaction link accepted')
          if (typeof onLinkedChange === 'function') onLinkedChange(data)
          setPayload((prev) =>
            prev
              ? {
                  ...prev,
                  payout: {
                    ...(prev.payout || {}),
                    transactionLinked: true,
                    linkedTransactionSummary: data?.linkedTransactionSummary || data,
                  },
                }
              : prev
          )
          setBrowseOpen(false)
          loadCandidates()
        }}
      />
    </div>
  )
}
